import React from "react";
import "./blogPage.css";
import RoundedLinkButton from "../../components/Buttons/RoundedLinkButton/RoundedLinkButton";
import SEOTags from "../../utils/SEOTags";
import { createSEOUrl } from "../../utils/createSEOUrl";
import { formatDate } from "../../utils/formatDate";
import MostRecent from "./components/MostRecent/MostRecent";
import { useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import Spinner from "../../components/Spinner/Spinner";
import Layout from "../../components/layout/Layout";
import { client } from "../../utils/sanity";
import { PortableText } from "@portabletext/react";
import { urlFor } from "../../utils/sanityImgBuilder";

const BlogPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const cache = useRef({});

  useEffect(() => {
    if (cache.current[slug]) {
      setPost(cache.current[slug].post);
      setRecentPosts(cache.current[slug].recent);
      setLoading(false);
      return;
    }

    client
      .fetch(`
      {
        "post": *[_type == "post" && slug.current == $slug][0] {
          title,
          content,
          publishedAt,
          mainImage,
          seoTitle,
          seoDescription
      },
        "recent": *[_type == "post" && slug.current != $slug]
          | order(publishedAt desc)[0...10] {
          _id,
          title,
          "slug": slug.current
      }
    }
    `,
        { slug },
      )
      .then((data) => {
        setPost(data.post);
        setRecentPosts(data.recent);
        setLoading(false);
        cache.current[slug] = { post: data.post, recent: data.recent };
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [slug]);


  if (loading)
    return (
      <div className="blog-page-spinner-wrapper">
        <Spinner />
      </div>
    );
  if (error) return <p>Error loading blog post</p>;

  if (!post) return <p>No blog post found.</p>;

  const plainText = Array.isArray(post.content)
    ? post.content
        .map((block) => block.children?.map((child) => child.text).join(""))
        .join(" ")
    : "";

  const words = plainText.split(/\s+/);

  let slicedDescription = "";
  let totalLength = 0;

  for (const word of words) {
    if (totalLength + word.length + 1 <= 150) {
      slicedDescription += (slicedDescription ? " " : "") + word;
      totalLength += word.length + 1;
    } else {
      break;
    }
  }

  slicedDescription =
    slicedDescription.length > 0 ? slicedDescription : plainText;

  const components = {
    types: {
      image: ({ value }) => (
        <img
          src={urlFor(value).width(600).url()}
          alt={value.alt || post.title}
          className="blogpage-image"
        />
      ),
    },
  };

  const BlogPostingMarkup = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title, // Fråga om detta (seoTitle eller title) ?
    description: slicedDescription, //Fråga om detta (seoDescription eller sliced content)
    image: post.mainImage,
    author: {
      "@type": "Person",
      name: "Robin Rushdi Al-Sálehi",
      jobTitle: "Founder & CEO",
      image: "https://www.vakansa.se/images/team/robin.png",
    },
    publisher: { "@id": "https://www.vakansa.se/#organization" },
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.vakansa.se/blogg/${createSEOUrl(slug)}`,
    },
  };

  return (
    <Layout>
      <div className="blogpage-outer">
        <SEOTags
          title={post.seoTitle || post.title}
          description={post.seoDescription || slicedDescription}
          canonicalUrl={`/blogg/${createSEOUrl(slug)}`}
          ogImage={post.mainImage}
          structuredData={BlogPostingMarkup}
        />
        <div className="blogpage-inner">
          <main className="blogpage-container">
            <h1 className="blogpage-header">{post.title}</h1>
            <span className="blogpage-date">
              {formatDate(post.publishedAt)}
            </span>
            <div className="blogpage-content">
              <PortableText value={post.content} components={components} />
            </div>
            <div className="blogpage-author">
              <p>
                Vakansa är en lokaldelningsplattform med en vision om att främja
                en hållbar stadsutveckling, där vi möter människors lokalbehov,
                utan att det ska byggas nytt. Vår mission är att synliggöra och
                tillgängliggöra olika lokaltyper för alla möjliga tillfällen.
                Målet är att lokaler ska användas, inte stå tomma!
              </p>
              <div>
                <p>
                  Har ni lokaler ni önskar hyra eller hyra ut? Klicka nedan och
                  välkommen till Vakansa!
                </p>
                <RoundedLinkButton
                  label={"Hyr ut lokal"}
                  size="sm"
                  color="green"
                  href={"https://www.vakansa.se/hyr-ut-lokal"}
                />
                <RoundedLinkButton
                  label={"Hyr lokal"}
                  size="sm"
                  color="green"
                  href={"https://www.vakansa.se/lediga-lokaler"}
                />
              </div>
            </div>
            <RoundedLinkButton
              className="blogpage-back-btn"
              label={"Tillbaka"}
              href={"/blogg"}
              color="white"
              size="md"
            />
          </main>
          <MostRecent posts={recentPosts} />
        </div>
      </div>
    </Layout>
  );
};

export default BlogPage;
